/**
 * Multi-Tenant Permissions System
 * 
 * Defines roles and their associated permissions for the Site Sync application.
 * Roles: admin, manager, estimator, tech, office
 */

/**
 * @typedef {"admin" | "manager" | "estimator" | "tech" | "office"} UserRole
 */

/**
 * @typedef {Object} Permissions
 * @property {boolean} canManageSettings - Can modify company settings
 * @property {boolean} canManageUsers - Can add/edit/remove team members
 * @property {boolean} canManageBilling - Can manage subscription and billing
 * @property {boolean} canViewCustomers - Can view customer list and details
 * @property {boolean} canCreateCustomers - Can create new customers
 * @property {boolean} canEditCustomers - Can edit existing customers
 * @property {boolean} canDeleteCustomers - Can delete customers
 * @property {boolean} canViewEstimates - Can view estimates
 * @property {boolean} canCreateEstimates - Can create new estimates
 * @property {boolean} canEditEstimates - Can edit existing estimates
 * @property {boolean} canDeleteEstimates - Can delete estimates
 * @property {boolean} canApproveEstimates - Can approve/convert estimates to jobs
 * @property {boolean} canViewJobs - Can view jobs
 * @property {boolean} canCreateJobs - Can create new jobs
 * @property {boolean} canEditJobs - Can edit existing jobs
 * @property {boolean} canDeleteJobs - Can delete jobs
 * @property {boolean} canViewAllJobs - Can view all company jobs (vs only assigned)
 * @property {boolean} canViewSchedule - Can view the schedule calendar
 * @property {boolean} canEditSchedule - Can create/edit schedule items
 * @property {boolean} canAssignWork - Can assign work to teams/workers
 * @property {boolean} canViewInventory - Can view inventory items
 * @property {boolean} canEditInventory - Can add/edit/delete inventory items
 * @property {boolean} canManagePickLists - Can create pick lists and check items in/out
 * @property {boolean} canViewAnalytics - Can view analytics dashboard
 * @property {boolean} canViewAllAnalytics - Can view company-wide analytics (vs only own)
 */

/**
 * Permission definitions for each role
 */
export const rolePermissions = {
  admin: {
    // Full access to everything
    canManageSettings: true,
    canManageUsers: true,
    canManageBilling: true,
    canViewCustomers: true,
    canCreateCustomers: true,
    canEditCustomers: true,
    canDeleteCustomers: true,
    canViewEstimates: true,
    canCreateEstimates: true,
    canEditEstimates: true,
    canDeleteEstimates: true,
    canApproveEstimates: true,
    canViewJobs: true,
    canCreateJobs: true,
    canEditJobs: true,
    canDeleteJobs: true,
    canViewAllJobs: true,
    canViewSchedule: true,
    canEditSchedule: true,
    canAssignWork: true,
    canViewInventory: true,
    canEditInventory: true,
    canManagePickLists: true,
    canViewAnalytics: true,
    canViewAllAnalytics: true,
  },
  
  manager: {
    // Everything except billing and user management
    canManageSettings: true,
    canManageUsers: false,
    canManageBilling: false,
    canViewCustomers: true,
    canCreateCustomers: true,
    canEditCustomers: true,
    canDeleteCustomers: true,
    canViewEstimates: true,
    canCreateEstimates: true,
    canEditEstimates: true,
    canDeleteEstimates: true,
    canApproveEstimates: true,
    canViewJobs: true,
    canCreateJobs: true,
    canEditJobs: true,
    canDeleteJobs: true,
    canViewAllJobs: true,
    canViewSchedule: true,
    canEditSchedule: true,
    canAssignWork: true,
    canViewInventory: true,
    canEditInventory: true,
    canManagePickLists: true,
    canViewAnalytics: true,
    canViewAllAnalytics: true,
  },
  
  estimator: {
    // Focus on estimates and customers
    canManageSettings: false,
    canManageUsers: false,
    canManageBilling: false,
    canViewCustomers: true,
    canCreateCustomers: true,
    canEditCustomers: true,
    canDeleteCustomers: false,
    canViewEstimates: true,
    canCreateEstimates: true,
    canEditEstimates: true,
    canDeleteEstimates: true,
    canApproveEstimates: false, // Admin/Manager only
    canViewJobs: true,
    canCreateJobs: true, // Can convert estimates
    canEditJobs: false,
    canDeleteJobs: false,
    canViewAllJobs: true,
    canViewSchedule: true,
    canEditSchedule: false,
    canAssignWork: false,
    canViewInventory: true,
    canEditInventory: false,
    canManagePickLists: false,
    canViewAnalytics: true,
    canViewAllAnalytics: false,
  },
  
  tech: {
    // Field workers - limited access
    canManageSettings: false,
    canManageUsers: false,
    canManageBilling: false,
    canViewCustomers: true,
    canCreateCustomers: false,
    canEditCustomers: false,
    canDeleteCustomers: false,
    canViewEstimates: false,
    canCreateEstimates: false,
    canEditEstimates: false,
    canDeleteEstimates: false,
    canApproveEstimates: false,
    canViewJobs: true,
    canCreateJobs: false,
    canEditJobs: true, // Update status, add notes/photos
    canDeleteJobs: false,
    canViewAllJobs: false, // Only assigned jobs
    canViewSchedule: true,
    canEditSchedule: false,
    canAssignWork: false,
    canViewInventory: true,
    canEditInventory: false,
    canManagePickLists: false,
    canViewAnalytics: false,
    canViewAllAnalytics: false,
  },
  
  office: {
    // Administrative staff
    canManageSettings: false,
    canManageUsers: false,
    canManageBilling: false,
    canViewCustomers: true,
    canCreateCustomers: true,
    canEditCustomers: true,
    canDeleteCustomers: false,
    canViewEstimates: true,
    canCreateEstimates: true,
    canEditEstimates: true,
    canDeleteEstimates: false,
    canApproveEstimates: false,
    canViewJobs: true,
    canCreateJobs: true,
    canEditJobs: true,
    canDeleteJobs: false,
    canViewAllJobs: true,
    canViewSchedule: true,
    canEditSchedule: true,
    canAssignWork: true,
    canViewInventory: true,
    canEditInventory: false,
    canManagePickLists: false,
    canViewAnalytics: true,
    canViewAllAnalytics: false,
  },
};

/**
 * Get permissions for a given role
 * @param {UserRole} role - The user's role
 * @returns {Permissions} The permissions object
 */
export function getPermissionsForRole(role) {
  return rolePermissions[role] || rolePermissions.tech; // Default to most restrictive
}

/**
 * Check if a role has a specific permission
 * @param {UserRole} role - The user's role
 * @param {keyof Permissions} permission - The permission to check
 * @returns {boolean} True if the role has the permission
 */
export function hasPermission(role, permission) {
  const permissions = getPermissionsForRole(role);
  return permissions[permission] === true;
}

/**
 * List of all available roles
 */
export const AVAILABLE_ROLES = [
  { value: 'admin', label: 'Admin', description: 'Full access to all features' },
  { value: 'manager', label: 'Manager', description: 'Everything except billing and user management' },
  { value: 'estimator', label: 'Estimator', description: 'Focus on estimates and customers' },
  { value: 'tech', label: 'Tech', description: 'Field workers with limited access' },
  { value: 'office', label: 'Office', description: 'Administrative staff' },
];

